<template>
  <div class="pdf-elements-form" data-testid="pdf-elements-form">
    <form @submit.prevent="handleSubmit">
      <!-- General Information -->
      <Box :title="t('Informações gerais')" type="primary">
        <div class="form-fields">
          <!-- Name -->
          <div class="form-field">
            <label for="element-name">{{ t('Nome') }} *</label>
            <input-text
              elem-id="element-name"
              v-model="formData.name"
              :placeholder="t('Nome do elemento')"
              :disabled="isEditMode"
              data-testid="pdf-elements-name-input"
            />
            <small v-if="errors.name" class="error-message">{{ errors.name }}</small>
          </div>

          <!-- Type -->
          <div class="form-field">
            <label for="element-type">{{ t('Tipo') }} *</label>
            <select-list
              elem-id="element-type"
              v-model="formData.type"
              :itens-select="typeOptions"
              :placeholder="t('Escolha o tipo')"
              data-testid="pdf-elements-type-select"
            />
            <small v-if="errors.type" class="error-message">{{ errors.type }}</small>
          </div>
        </div>
      </Box>

      <!-- Image Upload -->
      <Box :title="t('Imagem')" type="primary">
        <!-- Image Preview -->
        <div v-if="imagePreview" class="image-preview">
          <img :src="imagePreview" alt="Preview" data-testid="pdf-elements-image-preview" />
        </div>

        <!-- File Upload -->
        <div class="file-upload-section">
          <input
            type="file"
            ref="fileInputRef"
            id="pdfElementImageInput"
            accept="image/*"
            data-testid="pdf-elements-file-input"
            @change="handleFileChange"
            style="display: none;"
          />
          <Button
            as="label"
            for="pdfElementImageInput"
            :label="t('Escolher arquivo')"
            icon="pi pi-upload"
            severity="warn"
            outlined
            data-testid="pdf-elements-choose-file-button"
          />
          <div class="file-info">
            <span v-if="selectedFileName">{{ selectedFileName }}</span>
            <span v-else class="muted-text">{{ t('Nenhum arquivo selecionado') }}</span>
          </div>
        </div>

        <small v-if="errors.data" class="error-message">{{ errors.data }}</small>
      </Box>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Box from '../../ui/Box.vue';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import { usePDFElements } from '../../composables/usePDFElements';

interface PDFElementFormData {
  name: string;
  type: string;
  data: string;
}

interface PDFElementApiData {
  name?: string;
  type?: string;
  data?: string;
}

interface SelectOption {
  ID: string;
  Name: string;
}

interface FormErrors {
  name?: string;
  type?: string;
  data?: string;
}

const { t } = useI18n();
const {
  fetchPDFElementByName,
  savePDFElement,
  convertFileToBase64
} = usePDFElements();

const props = defineProps<{
  elementName?: string | null;
}>();

const emit = defineEmits<{
  saved: [];
  cancel: [];
}>();

const formData = reactive<PDFElementFormData>({
  name: '',
  type: '',
  data: ''
});

const errors = reactive<FormErrors>({});
const isSaving = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const imagePreview = ref<string>('');
const selectedFileName = ref<string>('');
const fileInputRef = ref<HTMLInputElement | null>(null);

const typeOptions = computed<SelectOption[]>(() => [
  { ID: 'header', Name: t('Cabeçalho') },
  { ID: 'footer', Name: t('Rodapé') }
]);

const canSave = computed<boolean>(() => {
  return formData.name.trim() !== '' &&
         formData.type !== '' &&
         formData.data !== '';
});

const handleFileChange = async (event: Event): Promise<void> => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (!file) return;

  log('PDFElementsForm', 'handleFileChange', 'File selected', file.name);

  if (!file.type.startsWith('image/')) {
    errors.data = t('Por favor selecione um arquivo de imagem');
    return;
  }

  selectedFileName.value = file.name;

  try {
    const base64String = await convertFileToBase64(file) as string;
    log('PDFElementsForm', 'handleFileChange', 'File converted to base64');

    formData.data = base64String;
    imagePreview.value = base64String;

    if (errors.data) {
      delete errors.data;
    }
  } catch (error) {
    log('PDFElementsForm', 'handleFileChange', 'Error converting file', error);
    errors.data = t('Erro ao processar imagem');
  }
};

const initializeForNew = async (): Promise<void> => {
  log('PDFElementsForm', 'initializeForNew', 'Initializing form for new element');

  formData.name = '';
  formData.type = '';
  formData.data = '';

  isEditMode.value = false;
  imagePreview.value = '';
  selectedFileName.value = '';

  (Object.keys(errors) as Array<keyof FormErrors>).forEach(key => delete errors[key]);

  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

const loadForEdit = async (): Promise<void> => {
  if (!props.elementName) return;

  log('PDFElementsForm', 'loadForEdit', 'Loading element', props.elementName);

  try {
    const data = await fetchPDFElementByName(props.elementName) as PDFElementApiData;
    log('PDFElementsForm', 'loadForEdit', 'Loaded element', data);

    formData.name = data.name || '';
    formData.type = data.type || '';
    formData.data = data.data || '';

    isEditMode.value = true;
    imagePreview.value = data.data || '';
    selectedFileName.value = data.name ? t('Imagem atual') : '';
  } catch (error) {
    log('PDFElementsForm', 'loadForEdit', 'Error loading element', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar elemento'));
  }
};

const validateForm = (): boolean => {
  (Object.keys(errors) as Array<keyof FormErrors>).forEach(key => delete errors[key]);

  let isValid = true;

  if (!formData.name.trim()) {
    errors.name = t('Nome é obrigatório');
    isValid = false;
  }

  if (!formData.type) {
    errors.type = t('Tipo é obrigatório');
    isValid = false;
  }

  if (!formData.data) {
    errors.data = t('Imagem é obrigatória');
    isValid = false;
  }

  return isValid;
};

const handleSubmit = async (): Promise<void> => {
  log('PDFElementsForm', 'handleSubmit', 'Submitting form', {
    name: formData.name,
    type: formData.type,
    hasData: !!formData.data
  });

  if (!validateForm()) {
    log('PDFElementsForm', 'handleSubmit', 'Form validation failed');
    return;
  }

  isSaving.value = true;

  try {
    await savePDFElement({
      name: formData.name,
      type: formData.type,
      data: formData.data
    });

    log('PDFElementsForm', 'handleSubmit', 'Element saved successfully');
    eventBus.emit('globalMessage.success', t('Elemento salvo com sucesso'));
    emit('saved');
  } catch (error) {
    log('PDFElementsForm', 'handleSubmit', 'Error saving element', error);
    eventBus.emit('globalMessage.error', t('Erro ao salvar elemento'));
  } finally {
    isSaving.value = false;
  }
};

const handleCancel = (): void => {
  log('PDFElementsForm', 'handleCancel', 'Form cancelled');
  emit('cancel');
};

watch(() => props.elementName, async (newValue) => {
  if (newValue) {
    await loadForEdit();
  }
});

onMounted(async () => {
  log('PDFElementsForm', 'mounted', 'Component mounted', props.elementName);

  if (props.elementName) {
    await loadForEdit();
  } else {
    await initializeForNew();
  }
});

defineExpose({
  initializeForNew,
  loadForEdit,
  handleSubmit,
  canSave
});
</script>

<style scoped>
.pdf-elements-form {
  padding: 0;
}

/* Form Fields Container */
.form-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.form-field {
  flex: 1 1 calc(50% - 0.75rem);
  min-width: 280px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-field label {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--text-color);
}

/* Error Messages */
.error-message {
  color: var(--red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

/* Image Preview */
.image-preview {
  text-align: center;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  background-color: var(--surface-ground);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
}

.image-preview img {
  max-height: 100px;
  width: auto;
  border-radius: 4px;
}

/* File Upload Section */
.file-upload-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.file-info {
  flex: 1 1 auto;
  min-width: 200px;
}

.muted-text {
  color: var(--text-color-secondary);
  font-style: italic;
}

/* Responsive Design */
@media (max-width: 768px) {
  .form-field {
    flex: 1 1 100%;
    min-width: 100%;
  }

  .file-upload-section {
    flex-direction: column;
    align-items: stretch;
  }

  .file-upload-section :deep(.p-button) {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .form-fields {
    gap: 1rem;
  }
}
</style>
